import React, { type ReactNode } from 'react';
import groupBy from 'lodash/groupBy';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import {
	type DefaultCategoryInfo,
	useFormatCategoryName,
	DEFAULT_CATEGORIES,
} from '@atlassian/jira-software-releases-version-related-work-categories/src/common/ui/default-categories/CommunicationCategory.tsx';
import type {
	analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext$key,
	analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext$data,
} from './__generated__/analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext.graphql';

const isFirstPartyLink = (url: string): boolean =>
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	new URL(url).hostname === window.location.hostname;

type AnalyticsAttributes = {
	numLinks: number;
	hasNativeReleaseNotes: boolean;
	numConfluenceReleaseNotes: number;
	numOfLinkedIssues?: number;
	numPlaceholderItems: number;
	numNonPlaceholderItems: number;
	numFirstPartyLinks: number;
	numThirdPartyLinks: number;
	numLinksInCommunicationCategory?: number;
	numLinksInRolloutCategory?: number;
	numLinksInTestingCategory?: number;
	numLinksInAnalyticsCategory?: number;
	numLinksInDevelopmentCategory?: number;
	numLinksInSupportCategory?: number;
	numItemsInOtherCategories?: number;
	numCategories: number;
};

const useAnalyticsAttributes = (
	relatedWorkItems: analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext$data,
): AnalyticsAttributes => {
	const numberOfRelatedWorkItems = relatedWorkItems.length;
	const relatedWorkTypes = groupBy(relatedWorkItems, (item) => item.node?.__typename);
	const hasNativeReleaseNotes =
		relatedWorkTypes.JiraVersionRelatedWorkNativeReleaseNotes?.length > 0;
	const numConfluenceReleaseNotes =
		relatedWorkTypes.JiraVersionRelatedWorkConfluenceReleaseNotes?.length ?? 0;

	const getIssueLinkingAttributes = () => {
		const numOfLinkedIssues = relatedWorkItems.filter((item) => item.node?.issue !== null).length;
		return { numOfLinkedIssues };
	};

	const formatCategoryName = useFormatCategoryName();

	const getPlaceholderStateAttributes = () => {
		const genericLinks = relatedWorkTypes.JiraVersionRelatedWorkGenericLink ?? [];
		const numPlaceholderItems = genericLinks.filter((item) => item.node?.url === null).length;
		const numNonPlaceholderItems = genericLinks.length - numPlaceholderItems;
		const numFirstPartyLinks = genericLinks.filter(
			(item) => item.node?.url != null && isFirstPartyLink(item.node.url),
		).length;
		const numThirdPartyLinks = numNonPlaceholderItems - numFirstPartyLinks;
		const itemsByCategories = groupBy(relatedWorkItems, (item) => item.node?.category);

		// This is a temporary approach that won't properly recognise categories if the item was created in a different
		// locale from the one it's being viewed in. A more robust enum-based approach should be used once related work
		// items are migrated out of Data Depot and into ERS.
		const countItemsInCategory = (category: DefaultCategoryInfo) =>
			itemsByCategories[formatCategoryName(category)]?.length ?? 0;

		const defaultCategoryUsageCounts: [analyticsAttributeName: string, itemsInCategory: number][] =
			DEFAULT_CATEGORIES.map((category) => [
				category.analyticsAttributeName,
				countItemsInCategory(category),
			]);
		const numItemsInDefaultCategories = defaultCategoryUsageCounts.reduce(
			(runningTotal, [, itemsInCategory]) => runningTotal + itemsInCategory,
			0,
		);
		const numItemsInOtherCategories = numberOfRelatedWorkItems - numItemsInDefaultCategories;
		const numCategories = Object.keys(itemsByCategories).length;

		return {
			numPlaceholderItems,
			numNonPlaceholderItems,
			numFirstPartyLinks,
			numThirdPartyLinks,
			numItemsInOtherCategories,
			numCategories,
			...Object.fromEntries(defaultCategoryUsageCounts),
		};
	};

	return {
		// The `numLinks` name is slightly misleading, but we're keeping it for historical reasons
		numLinks: numberOfRelatedWorkItems,
		hasNativeReleaseNotes,
		numConfluenceReleaseNotes,
		// Note: when you clean up these flags, make the relevant fields in `AnalyticsAttributes`
		// required rather than optional.
		...getPlaceholderStateAttributes(),
		...getIssueLinkingAttributes(),
	};
};

type Props = {
	relatedWorkEdges: analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext$key;
	children: ReactNode;
};

export const RelatedWorkAnalyticsContext = ({
	relatedWorkEdges,
	children,
}: Props): React.JSX.Element => {
	const data =
		useFragment<analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment analyticsContext_softwareReleasesVersionRelatedWork_RelatedWorkAnalyticsContext on JiraVersionRelatedWorkV2Edge
				@relay(plural: true) {
					node {
						__typename
						issue {
							__typename
						}
						category
						... on JiraVersionRelatedWorkGenericLink {
							url
						}
					}
				}
			`,
			relatedWorkEdges,
		);

	const attributes = useAnalyticsAttributes(data);

	return (
		<ContextualAnalyticsData
			sourceName="versionRelatedWork"
			sourceType={SCREEN}
			attributes={attributes}
		>
			{children}
		</ContextualAnalyticsData>
	);
};

export default RelatedWorkAnalyticsContext;
